import type {
  AgentAudience,
  AgentPickerEntry,
  ModelCompany,
} from './agent-picker.query';

export type AgentPickerRow = {
  // The chat mode a pick selects
  mode: string;
  agent: string;
  alias: string | null;
  name: string;
  version: string | null;
  company: ModelCompany | null;
  role: string | null;
};

export type AgentPickerGroup = {
  kind: 'group';
  id: string;
  label: string;
  rows: AgentPickerRow[];
};

export type AgentPickerBlock =
  | AgentPickerGroup
  | { kind: 'older-toggle'; count: number; expanded: boolean };

type BuildAgentPickerBlocksArgs = {
  entries: AgentPickerEntry[];
  allowedModes: string[];
  recentModes: string[];
  query: string;
  showOlder: boolean;
};

// The chat modes that resolve per user instead of naming one agent
const FIXED_MODES: Record<string, { name: string; role: string }> = {
  default: { name: 'Default', role: 'Follows the Mode menu' },
  custom: { name: 'Custom', role: 'Your saved experimental mode' },
  fast: { name: 'Fast', role: 'Lite mode, kept for old clients' },
  'max-think': {
    name: 'Max think',
    role: 'Same as Default, kept for old clients',
  },
};
const OPEN_VERSION_GROUPS = 3;
const AUDIENCE_SECTIONS: [AgentAudience, string][] = [
  ['users', 'For users'],
  ['internal', 'Internal team'],
];
const RECENT_LIMIT = 4;

export const queryTokens = (query: string) =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

export const matchesMode = (row: AgentPickerRow, mode: string) =>
  row.mode === mode || row.agent === mode || row.alias === mode;

/** A multi-select pick: the row's agent leaves if it is selected under any of its modes, and joins otherwise */
export const toggleMode = (selectedModes: string[], row: AgentPickerRow) =>
  selectedModes.some((mode) => matchesMode(row, mode))
    ? selectedModes.filter((mode) => !matchesMode(row, mode))
    : [...selectedModes, row.mode];

/** Splits text into the parts that match a search token and the parts between them */
export const highlightMatches = (text: string, tokens: string[]) => {
  const lowered = text.toLowerCase();
  const marked = new Array<boolean>(text.length).fill(false);
  for (const token of tokens) {
    for (
      let start = lowered.indexOf(token);
      start !== -1;
      start = lowered.indexOf(token, start + token.length)
    ) {
      marked.fill(true, start, start + token.length);
    }
  }
  const parts: { text: string; match: boolean }[] = [];
  for (let index = 0; index < text.length; index++) {
    const last = parts.at(-1);
    if (last?.match === marked[index]) last.text += text[index];
    else parts.push({ text: text[index], match: marked[index] });
  }
  return parts;
};

/**
 * The picker's sections: with no search, recent picks, the agents users and
 * the internal team get today, and the newest prompt versions, with older
 * versions behind a toggle; with a search, every match grouped by version.
 * Each agent shows once.
 */
export const buildAgentPickerBlocks = ({
  entries,
  allowedModes,
  recentModes,
  query,
  showOlder,
}: BuildAgentPickerBlocksArgs): AgentPickerBlock[] => {
  const allowed = new Set(allowedModes);
  const agentRows = entries
    .map((entry) => toRow(entry, allowed))
    .filter((row) => allowed.has(row.mode));
  const agentModes = new Set(
    agentRows.flatMap((row) => [row.mode, row.agent, row.alias])
  );
  const otherRows = allowedModes
    .filter((mode) => !agentModes.has(mode))
    .map(modeRow);
  const tokens = queryTokens(query);

  if (tokens.length > 0) {
    const matches = (row: AgentPickerRow) =>
      tokens.every((token) => searchText(row).includes(token));
    return [
      ...versionGroups(agentRows.filter(matches)),
      group('other', 'Other modes', otherRows.filter(matches)),
    ].filter(hasRows);
  }

  const audiences = new Map(
    entries.map((entry) => [entry.agent, entry.audience])
  );
  const servesChatMode = (row: AgentPickerRow) => !!audiences.get(row.agent);
  const recent = [
    ...new Set(
      recentModes
        .map((mode) => agentRows.find((row) => matchesMode(row, mode)))
        .filter((row): row is AgentPickerRow => !!row)
        .filter((row) => !servesChatMode(row))
    ),
  ].slice(0, RECENT_LIMIT);
  const versions = versionGroups(
    agentRows.filter((row) => !servesChatMode(row) && !recent.includes(row))
  );
  const olderVersions = versions.slice(OPEN_VERSION_GROUPS);

  const blocks: AgentPickerBlock[] = [
    group('recent', 'Recent', recent),
    ...AUDIENCE_SECTIONS.map(([audience, label]) =>
      group(
        audience,
        label,
        agentRows.filter((row) => audiences.get(row.agent) === audience)
      )
    ),
    ...versions.slice(0, OPEN_VERSION_GROUPS),
  ];
  if (olderVersions.length > 0) {
    blocks.push({
      kind: 'older-toggle',
      count: olderVersions.reduce(
        (count, older) => count + older.rows.length,
        0
      ),
      expanded: showOlder,
    });
    if (showOlder) blocks.push(...olderVersions);
  }
  blocks.push(group('other', 'Other modes', otherRows));
  return blocks.filter(hasRows);
};

/** What the picker calls a chat mode that names no agent, e.g. "Default" for `default` */
export const fixedModeName = (mode: string) => FIXED_MODES[mode]?.name ?? mode;

/** The entry a chat mode selects, whether the mode holds its agent or its alias */
export const findAgentEntry = (
  entries: AgentPickerEntry[] | undefined,
  mode: string
) =>
  entries?.find((entry) => entry.agent === mode || entry.alias === mode) ??
  null;

const toRow = (
  entry: AgentPickerEntry,
  allowed: Set<string>
): AgentPickerRow => ({
  // An agent the user's chat modes list only by alias is selected by its alias
  mode: allowed.has(entry.agent) ? entry.agent : (entry.alias ?? entry.agent),
  agent: entry.agent,
  alias: entry.alias,
  name: entry.name,
  version: entry.version,
  company: entry.company,
  role: entry.role,
});

const modeRow = (mode: string): AgentPickerRow => ({
  mode,
  agent: mode,
  alias: null,
  name: fixedModeName(mode),
  version: null,
  company: null,
  role: FIXED_MODES[mode]?.role ?? null,
});

const searchText = (row: AgentPickerRow) =>
  [
    row.name,
    row.agent,
    row.alias,
    row.role,
    row.version && `v${row.version}`,
    row.company,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const group = (
  id: string,
  label: string,
  rows: AgentPickerRow[]
): AgentPickerGroup => ({ kind: 'group', id, label, rows });

const hasRows = (block: AgentPickerBlock) =>
  block.kind === 'older-toggle' || block.rows.length > 0;

// V8 point releases are few and old enough to share one group
const versionGroupId = (version: string | null) => {
  if (!version) return 'none';
  return version.split('.')[0] === '8' ? '8' : version;
};

const versionGroupLabel = (id: string) => {
  if (id === 'none') return 'Unversioned';
  if (id === '8') return 'V8.x';
  if (id === '9') return 'V9 previews';
  return `V${id}`;
};

const versionRank = (id: string) => {
  if (id === 'none') return -1;
  const [major, minor = '0'] = id.split('.');
  return Number(major) * 1000 + Number(minor);
};

const versionGroups = (rows: AgentPickerRow[]): AgentPickerGroup[] => {
  const rowsByGroup = new Map<string, AgentPickerRow[]>();
  for (const row of rows) {
    const id = versionGroupId(row.version);
    const groupRows = rowsByGroup.get(id) ?? [];
    groupRows.push(row);
    rowsByGroup.set(id, groupRows);
  }
  return [...rowsByGroup.entries()]
    .sort(([left], [right]) => versionRank(right) - versionRank(left))
    .map(([id, groupRows]) =>
      group(`version-${id}`, versionGroupLabel(id), groupRows)
    );
};
